import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  ArrowCounterClockwiseIcon,
  CaretDownIcon,
  CaretLeftIcon,
  ChatCircleDotsIcon,
  ChatCircleIcon,
  CheckIcon,
  PaperPlaneRightIcon,
  PushPinIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import {
  closeClientEscalations,
  fetchClient,
  fetchClients,
  release,
  replyClient,
  setReplyChannel,
  takeover,
  toReplyFailure,
  type ChatMessage,
  type ClientChannel,
  type ClientDetail,
  type ClientSummary,
  type ReplyChannelState,
} from '@/features/dialogs/api';
import {
  ChannelLabel,
  ChannelMark,
  byChannelOrder,
  inChannel,
  intoTransport,
} from '@/features/dialogs/ChannelMark';
import { ChannelPicker, ChannelPickerFooter } from '@/features/dialogs/ChannelPicker';
import {
  Button,
  Card,
  EmptyState,
  ListGroupHeader,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SkeletonRows,
  StatusChip,
  ThreadSeparator,
  cn,
  fmtMoney,
  fmtRelativeTime,
  fmtWhen,
} from '@smena/ui';
import { humanizeEscalationReason } from '@/lib/humanize-escalation';

export function DialogsPage() {
  const clientsQ = useQuery({
    queryKey: ['clients'],
    queryFn: fetchClients,
    refetchInterval: 5_000,
  });

  const { guestId } = useParams<{ guestId: string }>();
  const [selectedKey, setSelectedKey] = useState<string | null>(guestId ?? null);

  // Синхронизация URL → selectedKey: клик по эскалации ведёт /dialogs/:clientKey,
  // useParams обновляется, DialogPane перерисовывается через key={selectedKey}.
  useEffect(() => {
    if (guestId && guestId !== selectedKey) setSelectedKey(guestId);
  }, [guestId, selectedKey]);

  // Первым делом выбираем клиента при первой загрузке, если ещё не выбран (без URL param'а).
  // На телефоне не выбираем: там список и переписка - два отдельных экрана, и человек
  // должен сам решить, в какой диалог зайти.
  useEffect(() => {
    const wide = window.matchMedia('(min-width: 768px)').matches;
    if (wide && !selectedKey && clientsQ.data && clientsQ.data.length > 0) {
      setSelectedKey(clientsQ.data[0]!.clientKey);
    }
  }, [clientsQ.data, selectedKey]);

  // Приём из прототипа: список сам делится на то, что ждёт человека, и то, что идёт само.
  // Оператору не нужно читать все диалоги подряд - сверху ровно то, где он нужен.
  const clients = clientsQ.data ?? [];
  const needsYou = clients.filter((c) => c.hasEscalation || c.humanTookOver);
  const running = clients.filter((c) => !c.hasEscalation && !c.humanTookOver);

  return (
    <div className="grid h-full min-h-0 grid-cols-1 gap-4 md:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[300px_minmax(0,1fr)_290px]">
      <Card className={cn('min-h-0 overflow-hidden', selectedKey && 'hidden md:block')}>
        <div className="flex h-full min-h-0 flex-col">
          <div className="flex items-baseline justify-between gap-2 border-b border-line px-4 py-3">
            <div className="font-semibold">Диалоги</div>
            <div className="text-xs text-muted">{clients.length ? `${clients.length} всего` : ''}</div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {clientsQ.isLoading ? (
              <SkeletonRows rows={5} />
            ) : clients.length === 0 ? (
              <EmptyState
                title="Диалогов пока нет"
                description="Они появятся, как только гость напишет в Telegram или откроет ссылку на чат."
              />
            ) : (
              <>
                {needsYou.length > 0 ? (
                  <ListGroupHeader tone="urgent" title="Нужен ваш ответ" count={needsYou.length} />
                ) : (
                  <div className="flex items-center gap-2 border-b border-line bg-green/10 px-4 py-2.5 text-sm font-semibold text-green">
                    <CheckIcon weight="bold" className="size-4 shrink-0" />
                    Smena справляется сама
                  </div>
                )}
                {needsYou.map((c) => (
                  <ClientRow
                    key={c.clientKey}
                    client={c}
                    active={selectedKey === c.clientKey}
                    onClick={() => setSelectedKey(c.clientKey)}
                  />
                ))}
                {running.length > 0 ? (
                  <>
                    <ListGroupHeader title="Идёт само" count={running.length} />
                    {running.map((c) => (
                      <ClientRow
                        key={c.clientKey}
                        client={c}
                        active={selectedKey === c.clientKey}
                        onClick={() => setSelectedKey(c.clientKey)}
                      />
                    ))}
                  </>
                ) : null}
              </>
            )}
          </div>
        </div>
      </Card>

      {selectedKey ? (
        <DialogPane key={selectedKey} clientKey={selectedKey} onBack={() => setSelectedKey(null)} />
      ) : (
        <Card className="hidden min-h-0 overflow-hidden md:block">
          <EmptyState
            icon={<ChatCircleDotsIcon weight="bold" />}
            title="Выберите диалог слева"
            description="Сверху те, где Smena ждёт вашего ответа."
          />
        </Card>
      )}
    </div>
  );
}

/** Канал последнего входящего: именно он отвечает на вопрос «откуда пишет гость». */
function inboundChannel(client: ClientSummary): ClientChannel | null {
  const byRef = client.lastInbound
    ? client.channels.find((c) => c.channelRef === client.lastInbound!.channelRef)
    : null;
  return byRef ?? client.channels[0] ?? null;
}

function ClientRow({
  client,
  active,
  onClick,
}: {
  client: ClientSummary;
  active: boolean;
  onClick: () => void;
}) {
  const last = client.lastMessage;
  const needsYou = client.hasEscalation || client.humanTookOver;
  const initials = (client.guestName ?? '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

  const ch = inboundChannel(client);
  // «+N» считает только каналы, в которые гость писал: неоткрытая ссылка на веб-чат
  // каналом переписки не является.
  const others = client.channels.filter((c) => c.channelRef !== ch?.channelRef && !c.dormant);
  const down = ch?.status === 'down';

  // Скринридеру - то же, что глазу: кто, откуда, когда и статус. Пол гостя по имени не
  // угадываем: «пишет» верно и для Анны, и для Игоря.
  const ariaLabel = [
    client.guestName ?? 'Гость без имени',
    ch ? `${client.lastInbound ? 'пишет' : 'канал'} из ${ch.title}, ${ch.sub}` : '',
    fmtRelativeTime(client.lastActivityAt),
    // Превью последнего сообщения было доступно, пока имя кнопки собиралось из содержимого.
    // aria-label его заменяет целиком, поэтому текст возвращаем сюда явно.
    last ? `${last.role === 'user' ? 'последнее сообщение' : 'последний ответ'}: ${last.content}` : '',
    down ? 'канал не работает' : '',
    client.hasEscalation ? 'ждёт ответа' : '',
    client.humanTookOver ? 'отвечаете вы' : '',
  ]
    .filter(Boolean)
    .join(', ');

  // Строка списка - три уровня и не больше: кто и когда, о чём последнее сообщение,
  // и только при необходимости - статус.
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={cn(
        'flex w-full gap-3 border-b border-line px-3.5 py-3 text-left transition-colors',
        'border-l-2 border-l-transparent hover:bg-bg',
        active && 'border-l-accent bg-accent/8 hover:bg-accent/8',
        !active && needsYou && 'border-l-destructive/50',
      )}
    >
      <span
        className={cn(
          'mt-0.5 grid size-8 shrink-0 place-items-center rounded-full text-[11px] font-bold',
          needsYou ? 'bg-destructive/10 text-destructive' : 'bg-panel2 text-muted',
        )}
        aria-hidden
      >
        {initials || '?'}
      </span>

      <span className="min-w-0 flex-1" aria-hidden>
        <span className="flex items-baseline gap-2">
          <span className="min-w-0 truncate font-semibold">
            {client.guestName ?? 'Гость без имени'}
          </span>
          <span className="ml-auto shrink-0 text-[11px] text-muted">
            {fmtRelativeTime(client.lastActivityAt)}
          </span>
        </span>

        {last ? (
          <span className="mt-0.5 block truncate text-xs text-muted">
            {last.role === 'user' ? '' : '↳ '}
            {last.content}
          </span>
        ) : null}

        <span className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted">
          {ch ? <ChannelLabel channel={ch} size="sm" live className="text-[11px]" /> : null}
          {!client.lastInbound && ch ? <span>· гость ещё не писал</span> : null}
          {others.length ? (
            <span title={`также: ${others.map((c) => c.label).join(', ')}`}>+{others.length}</span>
          ) : null}
          {down ? (
            <StatusChip tone="amber" className="text-[11px]" title={ch?.reason ?? undefined}>
              <WarningIcon weight="bold" className="size-3" />
              не работает
            </StatusChip>
          ) : null}
          {client.hasEscalation ? (
            <StatusChip tone="red" className="text-[11px]">
              ждёт ответа
            </StatusChip>
          ) : null}
          {client.humanTookOver ? (
            <StatusChip tone="amber" className="text-[11px]">
              отвечаете вы
            </StatusChip>
          ) : null}
        </span>
      </span>
    </button>
  );
}

function DialogPane({ clientKey, onBack }: { clientKey: string; onBack: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const detailQ = useQuery({
    queryKey: ['clients', clientKey],
    queryFn: () => fetchClient(clientKey),
    refetchInterval: 3_000,
  });

  const [replyText, setReplyText] = useState('');
  /**
   * Какой авто-канал оператор видел, когда начал печатать. Если гость успел написать из
   * другого мессенджера, пилюля переключится - но молча этого делать нельзя, поэтому
   * появляется янтарная строка «Пока вы писали…».
   */
  const [seenAuto, setSeenAuto] = useState<string | null>(null);
  /**
   * Последний отказ сервера: код и его же текст. Код нужен, чтобы отличить настоящий сбой
   * доставки (красный, «Повторить») от смены адресата на лету (янтарный, это не поломка).
   */
  const [lastError, setLastError] = useState<{ code: string | null; message: string } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  // Автоскролл вниз при новых сообщениях
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [detailQ.data?.messages.length]);

  const d = detailQ.data;
  // Панель может оказаться новее api (окно выкладки). Тогда адресата считаем на клиенте
  // по тем же правилам - вечный скелетон вместо диалога был бы худшим исходом.
  const state = d ? (d.replyChannel ?? fallbackReplyState(d.client.channels)) : undefined;
  const channels = d?.client.channels ?? [];
  const live = channels.filter((c) => !c.dormant);
  const dormant = channels.filter((c) => c.dormant);
  const multi = live.length > 1;
  const effective = channels.find((c) => c.channelRef === state?.effective) ?? null;
  const autoChannel = channels.find((c) => c.channelRef === state?.auto) ?? null;

  // Пустое поле - забыть, какой канал был на старте набора: следующий раз отсчёт заново.
  useEffect(() => {
    if (!replyText.trim()) setSeenAuto(null);
  }, [replyText]);

  const replyMut = useMutation({
    mutationFn: () => replyClient(clientKey, replyText.trim(), state!.effective!, state!.auto),
    onSuccess: (res) => {
      setReplyText('');
      setSeenAuto(null);
      setLastError(null);
      void qc.invalidateQueries({ queryKey: ['clients', clientKey] });
      void qc.invalidateQueries({ queryKey: ['clients'] });
      // Канал берём из ответа сервера: он знает, куда сообщение ушло на самом деле.
      const sent = channels.find((c) => c.channelRef === res.channelRef);
      toast.success(sent ? `Отправлено ${inChannel(sent)}` : 'Сообщение отправлено');
    },
    onError: (err: unknown) => {
      // Текст ошибки приходит с сервера и совпадает с тем, что панель пишет сама:
      // одна таблица формулировок, иначе оператор видит два объяснения одной беды.
      const fail = toReplyFailure(err);
      setLastError({ code: fail.code, message: fail.message });
      void qc.invalidateQueries({ queryKey: ['clients', clientKey] });
      // Текст остаётся в поле - он ещё нужен: гость его не получил.
    },
  });

  const pinMut = useMutation({
    mutationFn: (channelRef: string | null) => setReplyChannel(clientKey, channelRef),
    onSuccess: () => {
      setPickerOpen(false);
      setSeenAuto(null);
      void qc.invalidateQueries({ queryKey: ['clients', clientKey] });
      void qc.invalidateQueries({ queryKey: ['clients'] });
      // Следующий шаг человека - печатать, поэтому фокус возвращаем в поле.
      requestAnimationFrame(() => taRef.current?.focus());
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const resolveMut = useMutation({
    mutationFn: () => closeClientEscalations(clientKey),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients', clientKey] });
      void qc.invalidateQueries({ queryKey: ['clients'] });
      void qc.invalidateQueries({ queryKey: ['attention'] });
      toast.success('Вопрос отмечен решённым');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const anyChannelTookOver = d?.client.humanTookOver;
  const takeoverMut = useMutation({
    mutationFn: async () => {
      if (!d) return;
      // берём/отпускаем все каналы клиента - staff.controller работает per-sid
      const sids = d.client.channels.map((c) => c.sid);
      const action = anyChannelTookOver ? release : takeover;
      await Promise.all(sids.map((sid) => action(sid)));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients', clientKey] });
      void qc.invalidateQueries({ queryKey: ['clients'] });
      toast.success(anyChannelTookOver ? 'Отдал агенту' : 'Взял диалог');
    },
  });

  const openEscalations = useMemo(() => d?.escalations ?? [], [d]);

  if (detailQ.isLoading || !d || !state) {
    return (
      <Card className="min-h-0 overflow-hidden">
        <SkeletonRows rows={6} className="p-5" />
      </Card>
    );
  }

  const warning = buildWarning({
    onOpenChannels: () => navigate('/integrations'),
    state,
    channels: live,
    effective,
    autoChannel,
    multi,
    draft: replyText,
    seenAuto,
    lastError,
    onPin: (ref) => pinMut.mutate(ref),
    onRetry: () => {
      setLastError(null);
      replyMut.mutate();
    },
    onOpenPicker: () => setPickerOpen(true),
  });
  const blocked = warning?.blocking ?? false;
  const canSend = !!state.effective && !blocked && !replyMut.isPending;

  function send() {
    if (!replyText.trim() || !canSend) return;
    replyMut.mutate();
  }

  return (
    <>
      <Card className="flex h-full min-h-0 flex-col overflow-hidden">
        {/* Header нити */}
        <div className="border-b border-line px-5 py-3">
          <div className="flex items-baseline justify-between gap-3">
            <button
              type="button"
              onClick={onBack}
              className="-ml-1 mt-0.5 shrink-0 self-start rounded-md p-1 text-muted hover:bg-panel2 hover:text-text md:hidden"
              aria-label="Назад к списку диалогов"
            >
              <CaretLeftIcon weight="bold" className="size-5" />
            </button>
            <div className="min-w-0 flex-1">
              <div className="truncate text-base font-semibold">
                {d.client.guestName ?? 'Гость без имени'}
              </div>
              <div className="mt-0.5 text-xs text-muted">
                {d.property.title}
                {d.property.city ? ` · ${d.property.city}` : ''} · {d.messages.length} сообщ.
              </div>
              {/* Пока карточка брони справа не помещается, ключевое из неё - строкой в шапке. */}
              {d.booking ? (
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted xl:hidden">
                  {d.booking.checkin ? <span>заезд {d.booking.checkin}</span> : null}
                  {d.booking.checkout ? <span>выезд {d.booking.checkout}</span> : null}
                  {d.booking.amount ? <span className="tabular-nums">{fmtMoney(d.booking.amount)}</span> : null}
                  {d.booking.prepaid !== undefined ? (
                    <StatusChip tone={d.booking.prepaid ? 'green' : 'amber'}>
                      {d.booking.prepaid ? 'оплачено' : 'не оплачено'}
                    </StatusChip>
                  ) : null}
                </div>
              ) : null}
            </div>
            {/* На широком экране это же действие живёт в карточке брони справа. */}
            <Button
              variant={anyChannelTookOver ? 'secondary' : 'outline'}
              size="sm"
              className="xl:hidden"
              onClick={() => takeoverMut.mutate()}
              disabled={takeoverMut.isPending}
            >
              {anyChannelTookOver ? 'Вернуть Smena' : 'Отвечать самому'}
            </Button>
          </div>
        </div>

        {/* Лента сообщений */}
        <div ref={chatRef} className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {d.messages.length === 0 ? (
            <EmptyState title="Сообщений пока нет" description="Здесь появится переписка гостя со Smena." />
          ) : (
            <MessageList messages={d.messages} channels={channels} multi={multi} />
          )}
        </div>

        {/* Разбор ситуации - вплотную к полю ответа, а не в шапке треда: причина нужна ровно
            в тот момент, когда человек начинает печатать, а не когда открывает диалог. */}
        {openEscalations.length > 0 ? (
          <div className="border-t border-l-4 border-line border-l-destructive bg-destructive/10 px-5 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <WarningIcon weight="bold" className="size-4 shrink-0 text-destructive" />
              <span className="text-sm font-semibold text-destructive">
                {humanizeEscalationReason(openEscalations[0]!.reason)}
              </span>
              <span className="ml-auto text-xs text-muted">
                {fmtRelativeTime(openEscalations[0]!.ts)}
              </span>
            </div>
            {/* Сообщение гостя не повторяем - оно и так последним пузырём прямо над полосой. */}
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
              <span>Ответ из поля ниже снимет этот статус сам.</span>
              {/* Вопрос бывает решён и вне панели: позвонили, написали сами, он отпал.
                  Тогда нужен способ закрыть карточку, не отправляя гостю пустую реплику. */}
              <Button
                variant="ghost"
                size="sm"
                className="-my-1 h-7 px-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => resolveMut.mutate()}
                disabled={resolveMut.isPending}
              >
                <CheckIcon weight="bold" /> Вопрос решён
              </Button>
            </div>
          </div>
        ) : null}

        {/* Композер */}
        <div className="border-t border-line px-5 py-3">
          <ReplyAddress
            state={state}
            effective={effective}
            autoChannel={autoChannel}
            channels={live}
            dormant={dormant}
            multi={multi}
            hidden={blocked && multi && state.mode !== 'pinned'}
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            onPick={(ref) => pinMut.mutate(ref)}
            onAuto={() => pinMut.mutate(null)}
          />

          {warning ? <WarningLine warning={warning} /> : null}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="flex items-end gap-2"
          >
            <textarea
              ref={taRef}
              value={replyText}
              onChange={(e) => {
                const had = replyText.trim().length > 0;
                setReplyText(e.target.value);
                // Первый символ фиксирует, какой авто-канал человек видел, начиная писать.
                if (!had && e.target.value.trim()) setSeenAuto(state.auto);
                if (lastError) setLastError(null);
                autoGrow(e.target);
              }}
              onKeyDown={(e) => {
                // Enter отправляет только с физической клавиатуры и не во время IME-набора.
                // На телефоне Shift нет, и перехват Enter лишил бы человека переноса строки.
                if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
                if (window.matchMedia('(max-width: 767px)').matches) return;
                e.preventDefault();
                send();
              }}
              rows={1}
              placeholder="Ответ гостю…"
              aria-label="Ответ гостю"
              /* На телефоне кегль поля 16px: при меньшем iOS зумит экран на фокусе. */
              className="max-h-35 min-h-11 flex-1 resize-none rounded-md border border-line-control bg-input px-3 py-2 text-base text-text placeholder:text-muted md:min-h-10 md:text-sm"
            />
            {/* Кнопка живёт, только пока есть что отправлять. Текста на ней нет: адресата
                называет строка над полем, а иконка-самолётик читается без слов. */}
            {replyText.trim() ? (
              <button
                type="submit"
                disabled={!canSend}
                title={blocked ? 'Нет рабочего канала' : effective ? `Отправить ${intoTransport(effective)}` : 'Отправить'}
                aria-label={effective ? `Отправить ${intoTransport(effective)}` : 'Отправить'}
                className={cn(
                  'grid size-11 shrink-0 place-items-center rounded-full bg-accent text-white shadow-blue transition-colors md:size-10',
                  'hover:bg-accent-hover disabled:pointer-events-none disabled:opacity-60',
                )}
              >
                <PaperPlaneRightIcon weight="fill" className="size-4.5" />
              </button>
            ) : null}
          </form>
          {!blocked ? (
            <div className="mt-1.5 hidden text-[11px] text-muted md:block">
              Enter - отправить{effective ? ` ${intoTransport(effective)}` : ''}, Shift+Enter - новая строка
            </div>
          ) : null}
        </div>
      </Card>

      <BookingPanel
        detail={d}
        state={state}
        onTakeover={() => takeoverMut.mutate()}
        takeoverPending={takeoverMut.isPending}
        onPick={(ref) => pinMut.mutate(ref)}
        onAuto={() => pinMut.mutate(null)}
        className="hidden xl:flex"
      />
    </>
  );
}

/** Адресат по данным старого api: канал последнего входящего, без закрепления. */
function fallbackReplyState(channels: ClientChannel[]): ReplyChannelState {
  const live = channels.filter((c) => !c.dormant && c.status !== 'down');
  const auto =
    live.slice().sort((a, b) => ((b.lastInboundAt ?? '') < (a.lastInboundAt ?? '') ? -1 : 1))[0] ?? null;
  return {
    auto: auto?.channelRef ?? null,
    pinned: null,
    pinnedBy: null,
    effective: auto?.channelRef ?? null,
    mode: auto ? 'auto' : 'blocked',
    canSend: !!auto,
  };
}

/** Поле растёт под текст до пяти строк, дальше - прокрутка внутри. */
function autoGrow(el: HTMLTextAreaElement): void {
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
}

// ---------------------------------------------------------------- строка адресата

function ReplyAddress({
  state,
  effective,
  autoChannel,
  channels,
  dormant,
  multi,
  hidden,
  open,
  onOpenChange,
  onPick,
  onAuto,
}: {
  state: ReplyChannelState;
  effective: ClientChannel | null;
  autoChannel: ClientChannel | null;
  channels: ClientChannel[];
  dormant: ClientChannel[];
  multi: boolean;
  hidden: boolean;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onPick: (ref: string) => void;
  onAuto: () => void;
}) {
  const isMobile = useIsMobile();
  if (hidden || !effective) return null;

  const pinned = state.mode === 'pinned';
  const suffix = pinned
    ? '· выбрано вами'
    : state.mode === 'fallback'
      ? ''
      : autoChannel?.lastInboundAt
        ? `· куда гость писал последним, ${fmtWhen(autoChannel.lastInboundAt)}`
        : '· гость ещё не писал';

  // Один канал - переключать нечего: статичный текст без каретки и без поповера.
  if (!multi) {
    return (
      <div className="mb-2 flex min-h-6 flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        <span className="text-muted">Ответ уйдёт {effective.transport === 'vk' ? 'во' : 'в'}</span>
        <ChannelLabel channel={effective} live />
        <span className="hidden text-muted md:inline">
          {effective.lastInboundAt ? `· гость писал ${fmtWhen(effective.lastInboundAt)}` : '· гость ещё не писал'}
        </span>
      </div>
    );
  }

  const trigger = (
    <button
      type="button"
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={`Ответ уйдёт ${inChannel(effective)}${pinned ? ', выбрано вручную' : ''}. Выбрать канал`}
      title={[effective.label, effective.identity].filter(Boolean).join(' · ')}
      className={cn(
        'inline-flex max-w-[60%] items-center gap-1.5 rounded-md px-1.5 py-1 text-xs transition-colors md:h-7',
        'min-h-11 md:min-h-0',
        pinned ? 'bg-accent-soft text-accent-fg' : 'bg-panel2 hover:bg-accent/8',
      )}
    >
      {pinned ? <PushPinIcon weight="fill" className="size-3.5 shrink-0" /> : null}
      <ChannelLabel channel={effective} live className={pinned ? '[&_.text-muted]:text-accent-fg' : ''} />
      <CaretDownIcon weight="bold" className={cn('size-3 shrink-0', pinned ? 'text-accent-fg' : 'text-muted')} />
    </button>
  );

  const picker = (
    <ChannelPicker
      channels={channels}
      dormant={dormant}
      state={state}
      onPick={onPick}
      onAuto={onAuto}
      labelledBy="reply-picker-title"
    />
  );

  return (
    <div className="mb-2 flex min-h-6 flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      <span className="text-muted" aria-hidden>
        Ответ уйдёт {effective.transport === 'vk' ? 'во' : 'в'}
      </span>

      {isMobile ? (
        <>
          <span onClick={() => onOpenChange(true)}>{trigger}</span>
          <BottomSheet open={open} onOpenChange={onOpenChange} title="Куда отправить ответ">
            {picker}
            <ChannelPickerFooter />
          </BottomSheet>
        </>
      ) : (
        <Popover open={open} onOpenChange={onOpenChange}>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          <PopoverContent className="w-90" aria-label="Куда отправить ответ">
            <div
              id="reply-picker-title"
              className="px-2.5 pt-2 pb-1.5 text-[11px] font-bold tracking-wide text-muted uppercase"
            >
              Куда отправить ответ
            </div>
            {picker}
            <ChannelPickerFooter />
          </PopoverContent>
        </Popover>
      )}

      {pinned ? (
        <>
          <span className="hidden text-muted md:inline">{suffix}</span>
          <button
            type="button"
            onClick={onAuto}
            className="inline-flex min-h-11 items-center gap-1 py-1 font-semibold text-accent-fg hover:underline md:min-h-0"
          >
            <ArrowCounterClockwiseIcon weight="bold" className="size-3.5" />
            Вернуть авто
          </button>
        </>
      ) : (
        <span className="hidden text-muted md:inline">{suffix}</span>
      )}
    </div>
  );
}

/** Нижняя шторка вместо поповера на телефоне: поповер под клавиатурой съел бы ленту. */
function BottomSheet({
  open,
  onOpenChange,
  title,
  children,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-ink/40" />
        <DialogPrimitive.Content className="fixed inset-x-0 bottom-0 z-50 max-h-[70dvh] overflow-y-auto rounded-t-2xl bg-panel p-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] shadow-pop">
          <div className="mx-auto mt-1 mb-2 h-1 w-9 rounded-full bg-line-control/50" aria-hidden />
          {/* id не задаём: Radix ставит свой и связывает им контент. Наш перетирался,
              и aria-labelledby у шторки указывал в никуда. */}
          <DialogPrimitive.Title className="px-2.5 pb-1.5 text-[11px] font-bold tracking-wide text-muted uppercase">
            {title}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Выберите канал, в который уйдёт ваш ответ гостю.
          </DialogPrimitive.Description>
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const on = () => setMobile(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return mobile;
}

// ---------------------------------------------------------------- предупреждения

interface WarningAction {
  label: string;
  onClick: () => void;
}

interface WarningView {
  tone: 'red' | 'amber';
  text: string;
  actions: WarningAction[];
  /** Красное = отправлять нельзя: кнопка гаснет до переключения канала. */
  blocking: boolean;
}

/**
 * Ровно одно предупреждение за раз, по приоритету: нельзя отправить > не доставлено >
 * запасной канал > расхождение с пином > канал сменился, пока печатали.
 *
 * Это чистая функция от состояния, а не тост по событию: панель перезапрашивает диалог
 * раз в три секунды, и любой таймер тут заставил бы строку мигать.
 */
function buildWarning(input: {
  onOpenChannels: () => void;
  state: ReplyChannelState;
  channels: ClientChannel[];
  effective: ClientChannel | null;
  autoChannel: ClientChannel | null;
  multi: boolean;
  draft: string;
  seenAuto: string | null;
  lastError: { code: string | null; message: string } | null;
  onPin: (ref: string | null) => void;
  onRetry: () => void;
  onOpenPicker: () => void;
}): WarningView | null {
  const { state, channels, effective, autoChannel, multi, draft, seenAuto, lastError } = input;
  const goto = (ref: string) => ({ label: '', onClick: () => input.onPin(ref) });
  const backToAuto: WarningAction = { label: 'Вернуть авто', onClick: () => input.onPin(null) };
  const checkChannels = (one: boolean): WarningAction => ({
    label: one ? 'Проверить канал' : 'Проверить каналы',
    onClick: input.onOpenChannels,
  });
  const anyAlive = channels.some((c) => c.status !== 'down');
  const alt = channels.find((c) => c.channelRef !== state.effective && c.status !== 'down' && c.lastInboundAt);

  if (multi && !anyAlive) {
    return {
      tone: 'red',
      blocking: true,
      text: `Ни один канал гостя сейчас не работает: ${channels.map((c) => c.label).join(' и ')}. Текст останется в поле.`,
      actions: [checkChannels(false)],
    };
  }

  if (state.mode === 'pinned' && effective?.status === 'down') {
    return {
      tone: 'red',
      blocking: true,
      text: `${effective.label} сейчас не работает - сообщение не уйдёт.`,
      actions: [
        ...(alt ? [{ ...goto(alt.channelRef), label: `Отвечать ${intoTransport(alt)}` }] : []),
        backToAuto,
      ],
    };
  }

  if (!multi && effective?.status === 'down') {
    return {
      tone: 'red',
      blocking: true,
      text: `${effective.label} сейчас не работает - сообщение не уйдёт. Текст останется в поле.`,
      actions: [checkChannels(true)],
    };
  }

  if (lastError && draft.trim()) {
    // Смена адресата на лету - не поломка: гость просто написал из другого мессенджера.
    // Красная плашка с «Повторить» тут врала бы про сбой доставки.
    if (lastError.code === 'reply_target_changed') {
      const was = seenAuto ? channels.find((c) => c.channelRef === seenAuto) : null;
      return {
        tone: 'amber',
        blocking: false,
        text: lastError.message,
        actions: was ? [{ ...goto(was.channelRef), label: `Оставить ${was.title}` }] : [],
      };
    }
    if (lastError.code === 'channel_down') {
      return { tone: 'red', blocking: true, text: lastError.message, actions: [checkChannels(true)] };
    }
    // Без кода это не отказ сервера, а обрыв сети: показывать «Failed to fetch» оператору
    // незачем - он всё равно ничего с этим не сделает. Текст берём из таблицы постановки.
    const text =
      lastError.code || !effective
        ? lastError.message
        : `Не доставлено ${inChannel(effective)}. Текст остался в поле.`;
    return {
      tone: 'red',
      blocking: false,
      text,
      actions: [
        { label: 'Повторить', onClick: input.onRetry },
        ...(multi ? [{ label: 'Выбрать другой канал', onClick: input.onOpenPicker }] : []),
      ],
    };
  }

  if (state.mode === 'fallback' && autoChannel && effective) {
    const when = effective.lastInboundAt ? ` - гость писал туда ${fmtRelativeTime(effective.lastInboundAt)}` : '';
    return {
      tone: 'amber',
      blocking: false,
      text: `${autoChannel.label} сейчас не работает. Ответ уйдёт ${inChannel(effective)}${when}.`,
      actions: [checkChannels(true)],
    };
  }

  if (state.mode === 'pinned' && autoChannel && effective && state.auto !== state.effective) {
    return {
      tone: 'amber',
      blocking: false,
      text: `Гость последним писал из ${autoChannel.label}, а ответ уйдёт ${inChannel(effective)}.`,
      actions: [
        // Если авто-канал сам лежит, «Отвечать в …» обещал бы невозможное.
        autoChannel.status === 'down'
          ? backToAuto
          : { ...backToAuto, label: `Отвечать ${intoTransport(autoChannel)}` },
      ],
    };
  }

  if (state.mode === 'auto' && draft.trim() && seenAuto && seenAuto !== state.auto && autoChannel) {
    const was = channels.find((c) => c.channelRef === seenAuto);
    return {
      tone: 'amber',
      blocking: false,
      text: `Пока вы писали, гость написал из ${autoChannel.label} - ответ уйдёт туда.`,
      actions: was ? [{ ...goto(was.channelRef), label: `Оставить ${was.title}` }] : [],
    };
  }

  return null;
}

function WarningLine({ warning }: { warning: WarningView }) {
  return (
    <div
      role={warning.tone === 'red' ? 'alert' : 'status'}
      className={cn(
        'mb-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 rounded-md px-3 py-2 text-xs leading-snug',
        warning.tone === 'red' ? 'bg-destructive/10' : 'bg-amber/10',
      )}
    >
      <WarningIcon
        weight="bold"
        className={cn('size-4 shrink-0', warning.tone === 'red' ? 'text-destructive' : 'text-amber')}
      />
      <span className="min-w-[16ch] flex-1 text-text">{warning.text}</span>
      {warning.actions.map((a, i) => (
        <button
          key={a.label}
          type="button"
          onClick={a.onClick}
          className={cn(
            'min-h-11 py-1 font-semibold whitespace-nowrap text-accent-fg hover:underline md:min-h-0',
            i === 0 && 'ml-auto',
          )}
        >
          {a.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- лента

function MessageList({
  messages,
  channels,
  multi,
}: {
  messages: (ChatMessage & { channelRef: string })[];
  channels: ClientChannel[];
  multi: boolean;
}) {
  const byRef = useMemo(() => new Map(channels.map((c) => [c.channelRef, c])), [channels]);
  // Канал текущего отрезка ленты = канал последнего входящего выше. Исходящие отрезок
  // не переключают: ответ в другой канал - это как раз то, что надо подсветить.
  let segment: string | null = null;
  let prev: (ChatMessage & { channelRef: string }) | null = null;

  return (
    <div className="flex flex-col">
      {messages.map((m, i) => {
        const newDay =
          !!m.ts && (!prev?.ts || new Date(prev.ts).toDateString() !== new Date(m.ts).toDateString());
        const ch = byRef.get(m.channelRef) ?? null;
        const switched = multi && m.role === 'user' && m.channelRef !== segment;
        const first = segment === null;
        const grouped = !!prev && prev.role === m.role && !newDay && prev.channelRef === m.channelRef && !switched;
        const node = (
          <Fragment key={i}>
            {newDay && m.ts ? <ThreadSeparator>{dayLabel(m.ts)}</ThreadSeparator> : null}
            {switched && ch ? (
              <ThreadSeparator>
                <ChannelMark channel={ch} size="sm" />
                {first ? 'Гость написал' : 'Гость перешёл'} {inChannel(ch)}
              </ThreadSeparator>
            ) : null}
            <MessageBubble msg={m} channel={ch} grouped={grouped} offSegment={multi && m.role !== 'user' && m.channelRef !== segment} />
          </Fragment>
        );
        if (switched) segment = m.channelRef;
        prev = m;
        return node;
      })}
    </div>
  );
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  return d.toDateString() === new Date().toDateString()
    ? 'сегодня'
    : d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
}

function MessageBubble({
  msg,
  channel,
  grouped,
  offSegment,
}: {
  msg: ChatMessage & { channelRef?: string };
  channel: ClientChannel | null;
  /** Предыдущее сообщение от того же отправителя в том же канале - жмёмся плотнее. */
  grouped: boolean;
  /** Ответ ушёл не в тот канал, где гость писал последним, - помечаем прямо в ленте. */
  offSegment: boolean;
}) {
  const isGuest = msg.role === 'user';
  const isSystem = msg.role === 'system';
  const isStaff = msg.role === 'staff';
  if (isSystem) {
    return (
      <div className="mx-auto max-w-md rounded-full bg-panel2 px-3 py-1 text-center text-[11px] text-muted">
        {msg.content}
      </div>
    );
  }

  const time = msg.ts
    ? new Date(msg.ts).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    : null;

  // Гость слева, ответы справа. Оформление - как в гостевом веб-чате: плотный пузырь,
  // время внутри, без рамок. Оператор видит ровно то же, что видит гость.
  return (
    <div className={cn('flex', isGuest ? 'justify-start' : 'justify-end', grouped ? 'mt-0.5' : 'mt-2')}>
      <div
        title={channel ? [channel.label, channel.identity].filter(Boolean).join(' · ') : undefined}
        className={cn(
          'max-w-[78%] rounded-2xl px-3.5 py-2 text-sm leading-snug',
          isGuest
            ? cn('bg-panel2 text-text', !grouped && 'rounded-bl-md')
            : isStaff
              ? cn('bg-ink text-on-ink shadow-card', !grouped && 'rounded-br-md')
              : cn('bg-accent text-white shadow-blue', !grouped && 'rounded-br-md'),
        )}
      >
        {isStaff && !grouped ? (
          <div className="mb-0.5 flex items-center gap-1 text-[10px] font-semibold tracking-wider text-muted-on-ink uppercase">
            <ChatCircleIcon weight="fill" className="size-3" />
            Вы
          </div>
        ) : null}
        <span className="break-words whitespace-pre-wrap">{msg.content}</span>
        <span
          className={cn(
            'float-right mt-1 ml-2 inline-flex items-center gap-1.5 text-[10px] tabular-nums',
            isGuest ? 'text-muted' : isStaff ? 'text-muted-on-ink' : 'text-white/90',
          )}
        >
          {offSegment && channel ? <span>→ {channel.label}</span> : null}
          {time}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- карточка справа

/** Карточка гостя и брони справа от переписки - то, на что оператор смотрит, отвечая. */
function BookingPanel({
  detail,
  state,
  onTakeover,
  takeoverPending,
  onPick,
  onAuto,
  className,
}: {
  detail: ClientDetail;
  state: ReplyChannelState;
  onTakeover: () => void;
  takeoverPending: boolean;
  onPick: (ref: string) => void;
  onAuto: () => void;
  className?: string;
}) {
  const b = detail.booking;
  const took = detail.client.humanTookOver;
  const rows: Array<[string, string]> = [];
  if (b?.checkin) rows.push(['Заезд', b.checkin]);
  if (b?.checkout) rows.push(['Выезд', b.checkout]);
  if (b?.nights) rows.push(['Ночей', String(b.nights)]);
  if (b?.amount) rows.push(['Сумма', fmtMoney(b.amount)]);

  const channels = detail.client.channels.filter((c) => !c.dormant).sort(byChannelOrder);
  const dormant = detail.client.channels.filter((c) => c.dormant);
  const multi = channels.length > 1;

  return (
    <Card className={cn('flex min-h-0 flex-col overflow-y-auto', className)}>
      <div className="flex flex-col gap-4 p-4">
        <div>
          <div className="text-[11px] font-bold tracking-wide text-muted uppercase">Гость</div>
          <div className="mt-1 font-semibold">{detail.client.guestName ?? 'Гость без имени'}</div>
          <div className="mt-0.5 text-xs text-muted">
            {detail.property.title}
            {detail.property.city ? ` · ${detail.property.city}` : ''}
          </div>
        </div>

        {rows.length > 0 || b?.prepaid !== undefined ? (
          <div className="border-t border-line pt-3">
            <div className="text-[11px] font-bold tracking-wide text-muted uppercase">Бронь</div>
            <div className="mt-2 flex flex-col gap-1.5">
              {rows.map(([k, v]) => (
                <div key={k} className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="text-muted">{k}</span>
                  <span className="text-right font-semibold tabular-nums">{v}</span>
                </div>
              ))}
              {b?.prepaid !== undefined ? (
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="text-muted">Оплата</span>
                  <StatusChip tone={b.prepaid ? 'green' : 'amber'}>
                    {b.prepaid ? 'предоплата получена' : 'не оплачено'}
                  </StatusChip>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="border-t border-line pt-3">
          <div className="text-[11px] font-bold tracking-wide text-muted uppercase">Каналы</div>
          <div className="mt-2 flex flex-col gap-1">
            {channels.map((c) => {
              const isTarget = state.effective === c.channelRef && state.canSend;
              const down = c.status === 'down';
              return (
                <div
                  key={c.channelRef}
                  className={cn('-mx-2 flex gap-2.5 rounded-md p-2', isTarget && 'bg-accent-soft')}
                >
                  <ChannelMark channel={c} size="lg" live className="mt-0.5" />
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <div className="flex flex-wrap items-center gap-1.5 text-[13px] font-semibold">
                      <span className={cn('truncate', down && 'text-muted')}>{c.label}</span>
                      {isTarget ? (
                        <StatusChip
                          tone="accent"
                          className="text-[11px]"
                          title={state.mode === 'pinned' ? 'выбрано вами' : 'куда гость писал последним'}
                        >
                          {state.mode === 'pinned' ? <PushPinIcon weight="fill" className="size-3" /> : null}
                          ответ уйдёт сюда
                        </StatusChip>
                      ) : null}
                      {down ? (
                        <StatusChip tone="red" className="text-[11px]" title={c.reason ?? undefined}>
                          не работает
                        </StatusChip>
                      ) : null}
                    </div>
                    <div className="text-[11px] text-muted">
                      {[c.identity, c.lastInboundAt ? `гость писал ${fmtWhen(c.lastInboundAt)}` : 'гость не писал']
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                    {/* Действие отдельной кнопкой, а не кликом по всей строке: промах пальцем
                        не должен молча переадресовать ответ другому мессенджеру. */}
                    {multi && !down && !isTarget ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-ml-2 h-6 self-start px-2"
                        onClick={() => onPick(c.channelRef)}
                      >
                        Отвечать сюда
                      </Button>
                    ) : null}
                    {down ? (
                      <Button variant="link" size="sm" className="-ml-1 h-6 self-start px-1" asChild>
                        <a href="/integrations">Настроить канал</a>
                      </Button>
                    ) : null}
                  </div>
                </div>
              );
            })}

            {dormant.map((c) => (
              <div key={c.channelRef} className="-mx-2 flex gap-2.5 rounded-md p-2">
                <ChannelMark channel={c} size="lg" className="mt-0.5 opacity-70" />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <div className="truncate text-[13px] font-semibold text-muted">{c.label}</div>
                  <div className="text-[11px] text-muted">
                    {c.identity ? `${c.identity} · ` : ''}гость не открывал
                  </div>
                </div>
              </div>
            ))}
          </div>

          {state.mode === 'pinned' ? (
            <Button variant="link" size="sm" className="-ml-1 mt-1 h-6 px-1" onClick={onAuto}>
              <ArrowCounterClockwiseIcon weight="bold" /> Вернуть авто
            </Button>
          ) : null}
          {multi ? (
            <div className="mt-2 text-xs text-muted">
              Smena отвечает туда, откуда пришёл вопрос. Ваш выбор действует только на ваши сообщения.
            </div>
          ) : null}
        </div>

        {/* Кто сейчас отвечает - статус и действие одним блоком: раньше заголовок, чип и
            кнопка стояли тремя разными сущностями и читались как несвязанные. */}
        <div className={cn('rounded-md p-3', took ? 'bg-amber/10' : 'bg-green/10')}>
          <div className={cn('flex items-center gap-2 font-semibold', took ? 'text-amber' : 'text-green')}>
            <span className={cn('size-2 rounded-full', took ? 'bg-amber' : 'bg-green')} />
            {took ? 'Отвечаете вы' : 'Отвечает Smena'}
          </div>
          <div className="mt-1 text-xs leading-snug text-muted">
            {took
              ? 'Smena молчит в этом диалоге, пока вы не вернёте его ей.'
              : 'Ваш ответ снимает «нужен ответ», но диалог остаётся за Smena - следующий вопрос гостя она возьмёт сама.'}
          </div>
          <Button
            variant="secondary"
            size="sm"
            className="mt-2.5 w-full"
            onClick={onTakeover}
            disabled={takeoverPending}
          >
            {took ? 'Вернуть Smena' : 'Отвечать самому'}
          </Button>
        </div>
      </div>
    </Card>
  );
}
